/**
 * Live notification payload helpers (USER_WENT_LIVE).
 */

export function parseNotificationData(data) {
  if (!data) return null
  if (typeof data === 'object') return data
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data)
      return typeof parsed === 'object' && parsed ? parsed : null
    } catch {
      return null
    }
  }
  return null
}

export function getLiveStreamIdFromNotification(notification) {
  if (!notification) return null
  const data = parseNotificationData(notification.data)
  const id =
    notification.liveStreamId ||
    notification.liveStream?.id ||
    data?.liveStreamId
  return id ? String(id) : null
}

export function formatLiveActorName(actor, fallback = 'Someone') {
  if (!actor) return fallback
  const first = (actor.firstName || '').trim()
  const last = (actor.lastName || '').trim()
  if (first || last) return `${first} ${last}`.trim()
  return actor.username || fallback
}

export function getLiveNotificationMessageKey(notification) {
  const data = parseNotificationData(notification?.data)
  return data?.isReminder
    ? 'notifications.messages.USER_WENT_LIVE_REMINDER'
    : 'notifications.messages.USER_WENT_LIVE'
}

export function getLiveNotificationMessage(notification, t, fallbackName = 'Someone') {
  const userName = formatLiveActorName(notification?.actor, fallbackName)
  const key = getLiveNotificationMessageKey(notification)
  return `${userName} ${t(key)}`
}

export function isLiveNotificationType(type) {
  return type === 'USER_WENT_LIVE'
}

/**
 * Navigate to a live stream from a USER_WENT_LIVE notification.
 * @returns {Promise<boolean>} false if blocked (e.g. host declined to leave)
 */
export async function navigateToLiveFromNotification(notification, router, t) {
  const { useLiveStore } = await import('../store/liveStore')
  const { confirmHostLeaveAndEnd } = await import('./liveHostExitGuard')
  const liveStore = useLiveStore()
  const canLeave = await confirmHostLeaveAndEnd(liveStore, t)
  if (!canLeave) return false

  const liveStreamId = getLiveStreamIdFromNotification(notification)
  if (liveStreamId) {
    await router.push({ name: 'live.viewer.screen', params: { id: liveStreamId } })
    return true
  }
  await router.push({ name: 'live.feed' })
  return true
}

export function normalizeNotificationPayload(notification) {
  if (!notification) return notification
  const data = parseNotificationData(notification.data)
  if (data && data !== notification.data) {
    return { ...notification, data }
  }
  return notification
}
